import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Card, Badge, Form, Button, Alert } from 'react-bootstrap';
import { withRouter, Link } from 'react-router-dom';
import { AuthorizationCheck } from '../utils/AuthorizationCheck';
import Logo from './common/Logo';

const Dashboard = ({ auth, history }) => {
  const [tenantInfo, setTenantInfo] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Article input form state
  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Pagination for recent articles
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    if (auth && auth.id) {
      fetchDashboardData();
    }
  }, [auth, page]);

  const fetchDashboardData = async () => {
    try {
      // Fetch tenant info
      const tenantResponse = await fetch('/api/user/tenants', { credentials: 'include' });
      if (tenantResponse.ok) {
        const data = await tenantResponse.json();
        const activeTenant = data.tenants?.find(t => t.is_active) || data.tenants?.[0];

        setTenantInfo({
          tenant: activeTenant,
          totalTenants: data.tenants?.length || 0
        });
      }

      // Fetch user stats
      const usageResponse = await fetch('/api/user/usage', { credentials: 'include' });
      if (usageResponse.ok) {
        const data = await usageResponse.json();
        setUserStats(data.usage);
      }

      // Fetch recent articles with pagination
      const historyResponse = await fetch(`/api/user/history?page=${page}&limit=10`, { credentials: 'include' });
      if (historyResponse.ok) {
        const data = await historyResponse.json();
        setRecentArticles(data.articles || []);
        setPagination(data.pagination || null);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => (num || 0).toLocaleString();

  // Handle article URL submission
  const handleUrlSubmit = async (e) => {
    e.preventDefault();
    setUrlError('');

    if (!url.trim()) {
      setUrlError('Please enter a URL');
      return;
    }

    // Validate URL format
    try {
      new URL(url);
    } catch (err) {
      setUrlError('Please enter a valid URL');
      return;
    }

    if (url.length > 2000) {
      setUrlError('URL is too long (max 2000 characters)');
      return;
    }

    // Redirect to the processing page with the URL
    history.push(`/app/article-to-media?url=${encodeURIComponent(url)}`);
  };

  const truncateTitle = (title, maxLength = 60) => {
    if (!title) return 'Untitled';
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + '...';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const isTenantAdmin = tenantInfo?.tenant?.role === 'admin';
  const isTenantManager = tenantInfo?.tenant?.role === 'manager';
  const canManageTenant = isTenantAdmin || isTenantManager;

  // Check if user has access to dashboard
  const renderPage = AuthorizationCheck(auth);

  if (!renderPage) {
    return null;
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Dashboard - Spawnry</title>
      </Helmet>

      {/* Header background */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '100px 0 4rem'
      }} />

      <Container style={{ marginTop: '-2.5rem', paddingBottom: '3rem' }}>
        {/* Article to Media Input Form - Full Width */}
        <Row className="mb-4">
          <Col>
            <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{
                padding: '1.25rem 1.5rem',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Logo size={18} color="white" />
                </div>
                <div>
                  <h5 style={{ margin: 0, fontWeight: '600', color: '#1a1a2e' }}>Article to Media</h5>
                  <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Transform articles into audio, social posts, and summaries</p>
                </div>
              </div>
              <Card.Body className="p-4">
                <Form onSubmit={handleUrlSubmit}>
                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <Form.Control
                      type="url"
                      placeholder="Paste an article URL..."
                      value={url}
                      onChange={(e) => { setUrl(e.target.value); setUrlError(''); }}
                      disabled={submitting}
                      style={{
                        flex: 1,
                        padding: '0.75rem 1rem',
                        borderRadius: '10px',
                        border: '2px solid #e2e8f0',
                        fontSize: '0.95rem'
                      }}
                    />
                    <Button
                      type="submit"
                      disabled={submitting || !url.trim()}
                      style={{
                        padding: '0.75rem 1.5rem',
                        fontWeight: '600',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        border: 'none',
                        borderRadius: '10px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Spawn Media
                    </Button>
                  </div>
                  {urlError && (
                    <Alert variant="danger" className="mt-3 mb-0" style={{
                      background: 'rgba(239, 68, 68, 0.1)',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      color: '#dc2626',
                      borderRadius: '10px',
                      fontSize: '0.9rem'
                    }}>
                      {urlError}
                    </Alert>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Recent Articles and Stats Row */}
        <Row>
          <Col lg={8}>
            {/* Recent Articles */}
            <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
              <div style={{
                padding: '1.25rem 1.5rem',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                </div>
                <h5 style={{ margin: 0, fontWeight: '600', color: '#1a1a2e' }}>Recent Articles</h5>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border spinner-border-sm text-secondary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : recentArticles.length === 0 ? (
                <div className="text-center py-5">
                  <p style={{ color: '#64748b', margin: 0, marginBottom: '0.5rem' }}>No articles yet</p>
                  <p style={{ color: '#94a3b8', margin: 0, fontSize: '0.85rem' }}>Paste a URL above to get started</p>
                </div>
              ) : (
                <>
                  <div style={{ padding: '0.5rem' }}>
                    {recentArticles.map((article, idx) => (
                      <Link
                        key={idx}
                        to={`/app/article-to-media/${article.slug}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '1rem 1.25rem',
                          textDecoration: 'none',
                          borderRadius: '12px',
                          transition: 'background 0.2s',
                          marginBottom: idx < recentArticles.length - 1 ? '0.25rem' : 0
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#f8fafc'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: '500',
                            color: '#1a1a2e',
                            marginBottom: '0.25rem',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {truncateTitle(article.article_title)}
                          </div>
                          <div style={{
                            fontSize: '0.8rem',
                            color: '#94a3b8',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            flexWrap: 'wrap'
                          }}>
                            <span style={{
                              padding: '0.125rem 0.5rem',
                              background: '#f1f5f9',
                              borderRadius: '4px',
                              fontSize: '0.75rem'
                            }}>
                              {article.source_domain}
                            </span>
                            <span>{formatDate(article.created_at)}</span>
                            {article.status !== 'completed' && (
                              <Badge
                                bg={article.status === 'failed' ? 'danger' : 'warning'}
                                style={{ fontSize: '0.7rem' }}
                              >
                                {article.status}
                              </Badge>
                            )}
                            {canManageTenant && article.created_by && (
                              <span style={{ color: '#94a3b8' }}>
                                by {article.created_by.name}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background: '#f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#64748b',
                          flexShrink: 0,
                          marginLeft: '1rem'
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                            <polyline points="12 5 19 12 12 19"></polyline>
                          </svg>
                        </div>
                      </Link>
                    ))}
                  </div>

                  {pagination && pagination.totalPages > 1 && (
                    <div style={{
                      padding: '1rem 1.5rem',
                      borderTop: '1px solid #f1f5f9',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                        Page {pagination.page} of {pagination.totalPages}
                      </span>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={page === 1}
                          onClick={() => setPage(p => p - 1)}
                          style={{ borderRadius: '8px', padding: '0.375rem 0.75rem' }}
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          disabled={page === pagination.totalPages}
                          onClick={() => setPage(p => p + 1)}
                          style={{ borderRadius: '8px', padding: '0.375rem 0.75rem' }}
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          </Col>

          {/* Stats Sidebar */}
          <Col lg={4}>
            {/* Usage Stats */}
            <Card style={{ border: 'none', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
              <Card.Body className="p-4">
                <h5 style={{ fontWeight: '600', marginBottom: '1rem' }}>Your Stats</h5>

                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>
                    This Month
                  </div>
                  <div className="d-flex justify-content-between">
                    <div>
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>
                        {formatNumber(userStats?.monthlyArticles)}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>articles</div>
                    </div>
                    <div className="text-end">
                      <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1e293b' }}>
                        {formatNumber(userStats?.monthlyTokensUsed)}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>tokens used</div>
                    </div>
                  </div>
                </div>

                <hr style={{ margin: '1rem 0' }} />

                <div>
                  <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.25rem' }}>
                    All Time
                  </div>
                  <div className="d-flex justify-content-between">
                    <div>
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#64748b' }}>
                        {formatNumber(userStats?.totalArticles)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>articles</div>
                    </div>
                    <div className="text-end">
                      <div style={{ fontSize: '1.25rem', fontWeight: '600', color: '#64748b' }}>
                        {formatNumber(userStats?.totalTokensUsed)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>tokens</div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

      </Container>
    </>
  );
};

function mapStateToProps({ auth }) {
  return { auth };
}

export default withRouter(connect(mapStateToProps)(Dashboard));
